import { Image } from "@fluentui/react";
import axios from "axios";
import { useEffect, useState } from "react";
import { authService } from "../../services/AuthService";

const cacheName = "thumbnailCache";
const THUMBNAIL_TTL = 3600000; // 1h, matches the persistent cache entry lifetime

// DEX-573: dedup the Graph thumbnail `/url` call. The same driveItemId is rendered by
// many cards at once (a doc can appear in a grid, a card, and a version row — DocumentInGrid
// even renders two thumbnails for one id). The persistent Cache API only dedups *sequential*
// fetches; concurrent mounts all miss it and each fire the request. These module-level maps
// dedup app-wide: `memCache` is a synchronous result cache, `inFlight` shares one in-flight
// promise per driveItemId so simultaneous callers issue a single request.
const memCache = new Map<string, { url: string; timestamp: number }>();
const inFlight = new Map<string, Promise<string | null>>();

const getStoredThumbnail = async (driveItemId: string): Promise<string | null> => {
  const cache = await caches.open(cacheName);
  const cachedResponse = await cache.match(driveItemId);
  if (cachedResponse) {
    const { url, timestamp } = await cachedResponse.json();
    if (Date.now() - timestamp < THUMBNAIL_TTL) {
      return url;
    }
    cache.delete(driveItemId);
  }
  return null;
};

const fetchThumbnailUrl = async (
  driveItemId: string,
  accessToken: string
): Promise<string | null> => {
  // Persistent cache first (survives reloads); promote a hit into the in-memory cache.
  const cached = await getStoredThumbnail(driveItemId);
  if (cached) {
    memCache.set(driveItemId, { url: cached, timestamp: Date.now() });
    return cached;
  }
  try {
    const response = await axios.get(
      `https://graph.microsoft.com/v1.0/sites/${process.env.REACT_APP_SITEID}/drive/items/${driveItemId}/thumbnails/0/large/url`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    const url = response.data.value.split("&cb=")[0];
    memCache.set(driveItemId, { url, timestamp: Date.now() });

    const cache = await caches.open(cacheName);
    const cachedResponse = new Response(
      JSON.stringify({ url, timestamp: Date.now() }),
      { headers: { "content-type": "application/json" } }
    );
    await cache.put(new Request(driveItemId), cachedResponse);
    return url;
  } catch (error) {
    console.error("Error fetching or caching image:", error);
    return null;
  }
};

interface ThumbnailProps {
  driveItemId: string | undefined;
  hoverId?: boolean;
  height: number | string;
  width?: number | string;
  openFile?: any;
}

export const Thumbnails: React.FC<ThumbnailProps> = (props: ThumbnailProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [accessToken, setAccessToken] = useState("");

  useEffect(() => {
    const unsubscribe = authService.subscribeToTokenChanges(
      (newToken: string) => {
        console.log("Token updated in Thumbnails");
        setAccessToken(newToken);
      }
    );

    const getInitialToken = async () => {
      let token = await authService.getAccessToken();
      setAccessToken(token);
    }

    getInitialToken();

    // Clean up subscription when component unmounts
    return () => {
      unsubscribe();
    };
  }, []);

  const getImageUrl = async () => {
    const driveItemId = props.driveItemId!;

    // Synchronous in-memory hit — no async work, no request.
    const mem = memCache.get(driveItemId);
    if (mem && Date.now() - mem.timestamp < THUMBNAIL_TTL) {
      setThumbnailUrl(mem.url);
      return;
    }

    // Share one request per driveItemId across concurrently-mounted thumbnails.
    let request = inFlight.get(driveItemId);
    if (!request) {
      request = fetchThumbnailUrl(driveItemId, accessToken);
      inFlight.set(driveItemId, request);
      request.finally(() => inFlight.delete(driveItemId));
    }

    const url = await request;
    if (url) {
      setThumbnailUrl(url);
    }
  };

  useEffect(() => {
    if (props.driveItemId && accessToken) {
      getImageUrl();
    }
  }, [props.driveItemId, accessToken]);

  return (
    <Image
      src={thumbnailUrl}
      width={props.width ? props.width : "fit-content"}
      height={props.height}
      style={{
        ...(props.hoverId
          ? {
              filter: "brightness(32%)",
              borderTopRadius: "0.4rem",
            }
          : null),
      }}
      onClick={() => (props.openFile ? props.openFile() : null)}
    />
  );
};
